import { module as BaseModule } from 'modujs';

const SELECTORS = {
  trigger: '[data-all-types-environments-list-trigger]',
};

const CLASS_EXPANDED = 'is-expanded';
const CLASS_GRADIENT_FADED = 'is-gradient-faded';
const CLASS_CONTENT_REVEALED = 'is-content-revealed';
/** Gradient fades first, then panel grows */
const GRADIENT_FADE_MS = 220;
/** After gradient faded; slight buffer so links enable after panel grows */
const REVEAL_DELAY_MS = 350;

/** Collapsed: ~2 clear rows + 3rd row starting to fade (roughly half of 5 rows) */
const COLLAPSED_HEIGHT_FRACTION = 0.7;

export default class AllTypesEnvironmentsList extends BaseModule {
  init() {
    this.trigger = this.el.querySelector(SELECTORS.trigger);

    if (!this.trigger) {
      return;
    }

    this.revealTimeout = null;
    this.gradientFadeTimeout = null;
    this.handleTriggerClick = this.handleTriggerClick.bind(this);
    this.handleContentClick = this.handleContentClick.bind(this);
    this.trigger.addEventListener('click', this.handleTriggerClick);
    this.el.addEventListener('click', this.handleContentClick);

    this.setHeightVars();
  }

  setHeightVars() {
    const el = this.el;
    const prevMaxHeight = el.style.maxHeight;
    el.style.maxHeight = 'none';
    const fullHeight = el.scrollHeight;
    el.style.maxHeight = prevMaxHeight;
    const collapsedHeight = Math.round(fullHeight * COLLAPSED_HEIGHT_FRACTION);
    el.style.setProperty(
      '--all-types-list-collapsed-height',
      `${collapsedHeight}px`,
    );
    el.style.setProperty('--all-types-list-full-height', `${fullHeight}px`);
    // Gradient starts below the button so it only overlays the list
    const buttonHeight = this.trigger ? this.trigger.offsetHeight : 0;
    const gap = 0;
    el.style.setProperty(
      '--all-types-list-gradient-start',
      `${buttonHeight + gap}px`,
    );
  }

  /**
   * When collapsed, clicking the gradient area (list wrapper) should expand.
   * Only trigger if the click was not on the button itself.
   */
  handleContentClick(event) {
    if (this.el.classList.contains(CLASS_EXPANDED)) {
      return;
    }
    if (this.trigger && this.trigger.contains(event.target)) {
      return;
    }
    event.preventDefault();
    this.handleTriggerClick();
  }

  handleTriggerClick() {
    const willBeExpanded = !this.el.classList.contains(CLASS_EXPANDED);

    if (this.revealTimeout) {
      clearTimeout(this.revealTimeout);
      this.revealTimeout = null;
    }
    if (this.gradientFadeTimeout) {
      clearTimeout(this.gradientFadeTimeout);
      this.gradientFadeTimeout = null;
    }

    if (willBeExpanded) {
      // 1) Fade gradient away, then 2) grow panel and enable links
      this.el.classList.add(CLASS_GRADIENT_FADED);
      this.gradientFadeTimeout = setTimeout(() => {
        this.gradientFadeTimeout = null;
        this.el.classList.add(CLASS_EXPANDED);
        this.trigger.setAttribute('aria-expanded', 'true');
        this.revealTimeout = setTimeout(() => {
          this.el.classList.add(CLASS_CONTENT_REVEALED);
          this.revealTimeout = null;
        }, REVEAL_DELAY_MS - GRADIENT_FADE_MS);
      }, GRADIENT_FADE_MS);
    } else {
      this.el.classList.remove(
        CLASS_EXPANDED,
        CLASS_GRADIENT_FADED,
        CLASS_CONTENT_REVEALED,
      );
      this.trigger.setAttribute('aria-expanded', 'false');
    }
  }

  destroy() {
    if (this.revealTimeout) {
      clearTimeout(this.revealTimeout);
      this.revealTimeout = null;
    }
    if (this.gradientFadeTimeout) {
      clearTimeout(this.gradientFadeTimeout);
      this.gradientFadeTimeout = null;
    }
    this.el.removeEventListener('click', this.handleContentClick);
    if (this.trigger) {
      this.trigger.removeEventListener('click', this.handleTriggerClick);
    }
    this.trigger = null;
  }
}
